import TeamFriendButton from '../community/TeamFriendButton';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BadgeCheck, CalendarDays, Check, Clock, Copy, Plus, Search, UserPlus, Users, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { track } from '../../services/trackingService';
import {
  ChallengeNewOrganizerDraft, ChallengeOrganizerHit, ChallengeSummary, ChallengeTeam, ChallengeTeamOrganizer, displayBundesland, formatDayShort,
  formatKm, progressPercent, suggestTeamAbbreviation, teamInviteIntro, normalizeTeamAbbreviation, teamAbbreviationError,
  ChallengeTeamRequest, TEAM_REQUEST_TEXTS, teamJoinMode,
} from '@radevents/shared';
import { ChallengeArt } from './Motion';
import './winterchallenge.css';

/**
 * Teams der Winterchallenge (Stufe 2, `docs/bereiche/winterchallenge.md`). Beitreten geht über den
 * Einladungslink oder, ohne Link, über eine Anfrage, die der Gründer annimmt (seit 27.10.2026).
 */

export function TeamView({ team, inviteCode, currentTeamName, intro = null, busy = false, onJoin, onRequest, onWithdraw, onDecide, onFriend, friendBusy }: {
  team: ChallengeTeam;
  /** Eingeladen, aber noch nicht dabei: kurz erklären, worum es geht (`teamInviteIntro`). */
  intro?: ChallengeSummary | null;
  /** Aus `?code=` der Adresse: wer über den Einladungslink kommt, kann beitreten. */
  inviteCode?: string | null;
  /** Das Team, in dem ich schon bin: dann gibt es keinen Beitritt, ein Wechsel geht nicht. */
  currentTeamName?: string | null;
  /** Eine Anfrage läuft: Knöpfe nehmen keinen zweiten Klick an. */
  busy?: boolean;
  onJoin?: () => void;
  onRequest?: () => void;
  onWithdraw?: () => void;
  /** Nur für den Gründer. */
  onDecide?: (request: ChallengeTeamRequest, accept: boolean) => void;
  onFriend?: (member: ChallengeTeam['members'][number]) => void;
  friendBusy?: number | null;
}) {
  const mode = teamJoinMode(team, { inviteCode, currentTeamName });
  return (
    <div className="wc-page" style={{ maxWidth: 760 }}>
      <Link to="/winterchallenge" className="wc-link wc-link--back hide-mobile">
        <ArrowLeft size={17} aria-hidden />Zur Winterchallenge
      </Link>
      <section className="wc-card">
        <span className="wc-kicker">Team in der Winterchallenge</span>
        {/* Kürzel vor dem Namen, wie es in der Rangliste vor den Fahrern steht; ändern lässt es sich nicht (Phil, 25.10.2026). */}
        <h1 className="wc-h1 wc-h1--team">
          <span className="wc-team-abbr">[{team.abbreviation}]</span> {team.name}
        </h1>
        {team.description && <p className="wc-lead" style={{ fontSize: 15 }}>{team.description}</p>}

        <div className="wc-me-numbers wc-team-numbers">
          <div><strong>{formatKm(team.km)}</strong><span>km</span></div>
          <div><strong>{team.members.length}</strong><span>{team.members.length === 1 ? 'fährt mit' : 'fahren mit'}</span></div>
          {team.rank != null && <div><strong>{team.rank}</strong><span>Platz von {team.teamsTotal.toLocaleString('de-DE')}</span></div>}
        </div>

        {team.isMember && team.organizerPending && (
          <p className="wc-muted wc-pending"><Clock size={14} aria-hidden />
            Fährt für {team.organizerPending.name}, sobald der Verein deinen Zugang bestätigt hat.
          </p>
        )}
        {team.isMember && team.inviteUrl && <InviteBox url={team.inviteUrl} />}
        {mode === 'otherTeam' && currentTeamName && (
          <p className="wc-muted" style={{ marginTop: 16 }}>{TEAM_REQUEST_TEXTS.otherTeam(currentTeamName)}</p>
        )}
        {mode === 'invite' && onJoin && (
          <div className="wc-team-join">
            {intro && <TeamInviteIntro summary={intro} teamName={team.name} />}
            <button type="button" className="wc-btn wc-btn--block wc-cue" onClick={onJoin}>
              {intro ? teamInviteIntro(intro, team.name).join : 'Team beitreten'}<ArrowRight size={17} aria-hidden />
            </button>
          </div>
        )}
        {mode === 'request' && onRequest && (
          <div className="wc-team-join">
            {intro && <TeamInviteIntro summary={intro} teamName={team.name} />}
            <p className="wc-muted wc-pending" style={{ margin: '0 0 12px' }}><Users size={14} aria-hidden />{TEAM_REQUEST_TEXTS.requestHint}</p>
            <button type="button" className="wc-btn wc-btn--block" disabled={busy} onClick={onRequest}>
              <UserPlus size={17} aria-hidden />{intro ? TEAM_REQUEST_TEXTS.requestAndJoin : TEAM_REQUEST_TEXTS.request}
            </button>
          </div>
        )}
        {mode === 'pending' && (
          <div className="wc-team-join wc-request-state">
            <p className="wc-pending" style={{ margin: 0 }}><Clock size={15} aria-hidden />{TEAM_REQUEST_TEXTS.pending(team.name)}</p>
            {onWithdraw && <button type="button" className="wc-link" disabled={busy} onClick={onWithdraw}>{TEAM_REQUEST_TEXTS.withdraw}</button>}
          </div>
        )}
        {mode === 'declined' && <p className="wc-muted" style={{ marginTop: 16 }}>{TEAM_REQUEST_TEXTS.declined}</p>}
        {mode === 'closed' && <p className="wc-muted">Die Saison ist abgeschlossen. Teambeitritte sind nicht mehr möglich.</p>}
      </section>

      {team.requests && team.requests.length > 0 && (
        <TeamRequests requests={team.requests} busy={busy} onDecide={onDecide} />
      )}

      <section className="wc-card" aria-labelledby="wc-team-members">
        <h2 id="wc-team-members" className="wc-h2">Wer mitfährt</h2>
        <ol className="wc-board" style={{ marginTop: 0 }}>
          {team.members.map((m, i) => (
            <li key={m.participantId} className={`wc-board-row${m.isMe ? ' is-me' : ''}`}>
              <span className="wc-rank">{i + 1}</span>
              <div className="wc-board-main">
                <strong>{m.displayName}{m.isMe && <span className="wc-chip">du</span>}</strong>
                <span>{displayBundesland(m.bundesland)}</span>
              </div>
              <div className="team-member-actions">
                {team.isMember && !m.isMe && m.friendshipState && <TeamFriendButton state={m.friendshipState} name={m.displayName} busy={friendBusy != null} onClick={() => onFriend?.(m)} />}
                <span className="wc-board-km">{formatKm(m.km)}<small> km</small></span>
              </div>
            </li>
          ))}
        </ol>
        {team.members.length === 1 && (
          <p className="wc-muted">Noch allein im Team. Teil den Einladungslink, dann fahrt ihr zusammen.</p>
        )}
      </section>

      {/* Erst wer mitfährt, dann der Veranstalter (Phil, 25.10.2026). */}
      {team.organizer && <ClubBlock club={team.organizer} />}
    </div>
  );
}

/** Offene Beitrittsanfragen, nur für den Gründer: annehmen oder ablehnen. */
export function TeamRequests({ requests, busy = false, onDecide }: {
  requests: ChallengeTeamRequest[];
  busy?: boolean;
  onDecide?: (request: ChallengeTeamRequest, accept: boolean) => void;
}) {
  return (
    <section className="wc-card wc-requests" aria-labelledby="wc-team-requests">
      <h2 id="wc-team-requests" className="wc-h2" style={{ marginBottom: 4 }}>
        {TEAM_REQUEST_TEXTS.listTitle}<span className="wc-chip">{requests.length}</span>
      </h2>
      <p className="wc-muted" style={{ marginTop: 0 }}>{TEAM_REQUEST_TEXTS.listHint}</p>
      <ul className="wc-requests-list">
        {requests.map(r => (
          <li key={r.id} className="wc-request">
            <div className="wc-board-main">
              <strong>{r.displayName}</strong>
              <span>{[r.bundesland ? displayBundesland(r.bundesland) : null, `${formatKm(r.km)} km`].filter(Boolean).join(' · ')}</span>
            </div>
            <div className="wc-request-actions">
              <button type="button" className="wc-btn wc-btn--ghost" disabled={busy} onClick={() => onDecide?.(r, false)}>
                <X size={16} aria-hidden />{TEAM_REQUEST_TEXTS.decline}
              </button>
              <button type="button" className="wc-btn" disabled={busy} onClick={() => onDecide?.(r, true)}>
                <Check size={16} aria-hidden />{TEAM_REQUEST_TEXTS.accept}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Für Eingeladene, die noch nicht mitmachen: was die Winterchallenge ist, bevor sie beitreten. */
export function TeamInviteIntro({ summary, teamName }: { summary: ChallengeSummary; teamName: string }) {
  const t = teamInviteIntro(summary, teamName);
  return (
    <div className="wc-invite-intro">
      <span className="wc-kicker">{t.title}</span>
      <p className="wc-invite-intro-lead">{t.lead}</p>
      <ul>
        {t.points.map(point => <li key={point}><Check size={15} aria-hidden />{point}</li>)}
      </ul>
      <p className="wc-invite-intro-foot">
        {t.people && <>{t.people} </>}
        <Link to="/winterchallenge">So funktioniert's</Link>
      </p>
    </div>
  );
}

function InviteBox({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(url)
      .then(() => { setCopied(true); toast.success('Link kopiert'); })
      .catch(() => toast(url));
  };
  return (
    <div className="wc-invite">
      <span className="wc-field" style={{ gap: 4 }}>
        <span>Einladungslink</span>
      </span>
      <div className="wc-invite-row">
        <input readOnly value={url} aria-label="Einladungslink" onFocus={e => e.currentTarget.select()} />
        <button type="button" className="wc-btn" onClick={copy}>
          {copied ? <><Check size={16} aria-hidden />Kopiert</> : <><Copy size={16} aria-hidden />Kopieren</>}
        </button>
      </div>
    </div>
  );
}

/**
 * Der Verein hinter einem Team, auf der Teamseite: seine nächsten Events und der Weg auf seine
 * Seite. Damit lohnt sich die Challenge für den Verein — wer das Team sieht, sieht seine Events.
 */
export function ClubBlock({ club }: { club: ChallengeTeamOrganizer }) {
  return (
    <section className="wc-card wc-club" aria-labelledby="wc-club-title">
      <span className="wc-kicker">Fährt für</span>
      <h2 id="wc-club-title" className="wc-h2" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {club.name}{club.verified && <BadgeCheck size={18} aria-label="Verifizierter Veranstalter" className="wc-verified" />}
      </h2>
      {club.upcomingEvents.length > 0 ? (
        <>
          <span className="wc-club-label">Nächste Events</span>
          <ul className="wc-club-events">
            {club.upcomingEvents.map(e => (
              <li key={e.id}>
                <Link to={`/events/${e.id}`} onClick={() => track('EVENT', String(e.id), 'CLICK', 'winterchallenge')}>
                  <CalendarDays size={15} aria-hidden />
                  <span className="wc-club-date">{formatDayShort(e.eventDate)}</span>
                  <span className="wc-club-title">{e.title}</span>
                  {e.city && <span className="wc-club-city">{e.city}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="wc-muted" style={{ marginTop: 0 }}>Noch keine Events für die nächste Saison eingetragen.</p>
      )}
      <Link className="wc-link wc-link--arrow" to={`/veranstalter/${club.id}`}>
        Zur Veranstalterseite<ArrowRight size={15} aria-hidden />
      </Link>
    </section>
  );
}

export interface TeamDraft {
  /** Nur ohne Veranstalter; mit Veranstalter heißt das Team wie er. */
  name: string;
  abbreviation: string;
  description: string;
  /** Veranstalter aus dem Verzeichnis. */
  organizerId: number | null;
  /** Veranstalter fehlt im Verzeichnis: wird mit dem Team angelegt. */
  newOrganizer: ChallengeNewOrganizerDraft | null;
}

const EMPTY_ORGANIZER: ChallengeNewOrganizerDraft = { name: '', website: '', email: '', postalCode: '', city: '' };

/**
 * Team gründen. Zuerst, freiwillig, der Veranstalter: aus dem Verzeichnis suchen oder, wenn er
 * fehlt, gleich anlegen. **Mit Veranstalter heißt das Team wie er**, damit man ihn in der
 * Teamwertung erkennt statt eines ausgedachten Namens (Phil, 25.10.2026). Ohne Veranstalter gibt
 * man einen eigenen Teamnamen an. Verbunden wird ohne Zugang und ohne Rückfrage; nachträglich
 * verbinden lässt sich nichts.
 *
 * Bis zum 25.10.2026 stand vorneweg die Frage „Fahrt ihr für einen Verein? Ja/Nein“, dann kurz ein
 * eigener Abschnitt auf der Teamseite. Beides war Phil zu umständlich: das Suchfeld ist jetzt
 * einfach das erste Feld, wer keinen Veranstalter hat, lässt es leer.
 */
export function TeamCreateForm({ initial, club = null, hits: initialHits = null, query: initialQuery = '', initialCreating = false, showErrors = false, saving = false, onSearch, onSubmit, onCancel }: {
  initial?: Partial<TeamDraft>;
  /** Vorauswahl, etwa von der Veranstalterseite. */
  club?: ChallengeOrganizerHit | null;
  /** Design-Lab: Treffer, Suchbegriff und offenes „Anlegen“ vorgeben. */
  hits?: ChallengeOrganizerHit[] | null;
  query?: string;
  initialCreating?: boolean;
  showErrors?: boolean;
  /** Anfrage läuft: der Knopf sagt es und nimmt keinen zweiten Klick an. */
  saving?: boolean;
  onSearch?: (q: string) => Promise<ChallengeOrganizerHit[]>;
  onSubmit: (d: TeamDraft) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [abbreviation, setAbbreviation] = useState(initial?.abbreviation ?? '');
  const [abbreviationEdited, setAbbreviationEdited] = useState(!!initial?.abbreviation);
  const [chosen, setChosen] = useState<ChallengeOrganizerHit | null>(club);
  const [creating, setCreating] = useState(initialCreating);
  const [org, setOrg] = useState<ChallengeNewOrganizerDraft>({ ...EMPTY_ORGANIZER, name: initialCreating ? initialQuery : '' });
  const [query, setQuery] = useState(initialQuery);
  const [hits, setHits] = useState<ChallengeOrganizerHit[] | null>(initialHits);
  const [tried, setTried] = useState(showErrors);
  const run = useRef(0);
  const timer = useRef<number | null>(null);
  // Beim Verlassen der Seite darf die verzögerte Suche nicht mehr in den State schreiben.
  useEffect(() => () => { if (timer.current != null) window.clearTimeout(timer.current); }, []);

  // Der Name, unter dem das Team in der Wertung steht.
  const teamName = chosen ? chosen.name : creating ? org.name : name;
  const shownAbbreviation = abbreviationEdited ? abbreviation : suggestTeamAbbreviation(teamName);
  const nameError = teamName.trim().length < 3
    ? (creating ? 'Wie heißt der Veranstalter? Mindestens drei Zeichen.' : 'Wie soll das Team heißen? Mindestens drei Zeichen.')
    : null;
  const abbreviationError = teamAbbreviationError(shownAbbreviation);
  const blocked = !!chosen?.existingTeam;
  const ok = !nameError && !abbreviationError && !blocked;

  const search = (q: string) => {
    setQuery(q);
    if (q.trim().length < 2) { setHits(null); return; }
    const id = ++run.current;
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      if (id !== run.current || !onSearch) return;
      onSearch(q).then(r => { if (id === run.current) setHits(r); }).catch(() => { if (id === run.current) setHits([]); });
    }, 250);
  };
  const startCreating = () => { setOrg({ ...EMPTY_ORGANIZER, name: query.trim() }); setCreating(true); };
  const setOrgField = (key: keyof ChallengeNewOrganizerDraft) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOrg(o => ({ ...o, [key]: value }));
  };

  const submit = () => onSubmit({
    name: teamName.trim(), abbreviation: shownAbbreviation, description: '',
    organizerId: chosen?.id ?? null, newOrganizer: creating && !chosen ? org : null,
  });

  return (
    <form className="wc-card wc-form" noValidate onSubmit={e => { e.preventDefault(); setTried(true); if (ok) submit(); }}>
      <h2 className="wc-h2 wc-h2--form">Team gründen</h2>

      <div className="wc-field">
        <span>Veranstaltet ihr auch Events? <i>optional</i></span>
        {chosen ? (
          <div className="wc-chosen">
            <span className="wc-hit-main">
              <strong>{chosen.name}{chosen.verified && <BadgeCheck size={14} aria-hidden className="wc-verified" />}</strong>
              {chosen.city && <span>{chosen.city}</span>}
            </span>
            <button type="button" className="wc-link" onClick={() => setChosen(null)}>Ändern</button>
          </div>
        ) : creating ? (
          <div className="wc-subform ui-aufklappen">
            {/* Dieselben Felder wie „Veranstalter eintragen“ (`OrganizerAccessDialog`), nur der Name ist Pflicht. */}
            <label className="wc-field">
              <span>Name des Veranstalters</span>
              <input autoFocus value={org.name} maxLength={120} placeholder="RSV Beispiel e.V." aria-invalid={tried && !!nameError} onChange={setOrgField('name')} />
              {tried && nameError && <small className="wc-error">{nameError}</small>}
            </label>
            <label className="wc-field">
              <span>Website <i>optional</i></span>
              <input value={org.website} maxLength={300} inputMode="url" placeholder="https://www.rsv-beispiel.de" onChange={setOrgField('website')} />
            </label>
            <label className="wc-field">
              <span>E-Mail des Veranstalters <i>optional</i></span>
              <input value={org.email} maxLength={200} type="email" placeholder="info@rsv-beispiel.de" onChange={setOrgField('email')} />
            </label>
            <div className="wc-form-row wc-form-row--plz">
              <label className="wc-field">
                <span>PLZ <i>optional</i></span>
                <input value={org.postalCode} maxLength={10} inputMode="numeric" autoComplete="off" onChange={setOrgField('postalCode')} />
              </label>
              <label className="wc-field">
                <span>Ort <i>optional</i></span>
                <input value={org.city} maxLength={100} onChange={setOrgField('city')} />
              </label>
            </div>
            <button type="button" className="wc-link" style={{ justifySelf: 'start' }} onClick={() => setCreating(false)}>Doch im Verzeichnis suchen</button>
          </div>
        ) : (
          <>
            <span className="wc-input-icon">
              <Search size={15} aria-hidden />
              <input value={query} placeholder="Name oder Ort" onChange={e => search(e.target.value)} />
            </span>
            {hits && hits.length > 0 && (
              <ul className="wc-hits" role="listbox">
                {hits.map(h => (
                  <li key={h.id}>
                    <button type="button" role="option" aria-selected={false} className="wc-hit" onClick={() => setChosen(h)}>
                      <span className="wc-hit-main">
                        <strong>{h.name}{h.verified && <BadgeCheck size={14} aria-hidden className="wc-verified" />}</strong>
                        {h.city && <span>{h.city}</span>}
                      </span>
                      {h.existingTeam && <span className="wc-hit-note">hat schon ein Team</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {hits && (
              <button type="button" className="wc-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifySelf: 'start' }} onClick={startCreating}>
                <Plus size={15} aria-hidden />{hits.length === 0 ? `„${query}“ als Veranstalter anlegen` : 'Nicht dabei? Veranstalter anlegen'}
              </button>
            )}
            {!hits && <small className="wc-hint">Dann verbinde euer Team mit eurem Veranstalter.</small>}
          </>
        )}
      </div>

      {chosen?.existingTeam && (
        <p className="wc-note">
          Für {chosen.name} fährt schon das Team <Link to={`/winterchallenge/team/${chosen.existingTeam.slug}`}>{chosen.existingTeam.name}</Link>.
          Frag dort nach dem Einladungslink oder stell auf der Teamseite eine Beitrittsanfrage.
        </p>
      )}

      {!blocked && (
        <>
          {chosen || creating ? (
            <div className="wc-field">
              <span>Teamname</span>
              <p className="wc-fixed-name">{teamName.trim() || 'Wie der Veranstalter'}</p>
            </div>
          ) : (
            <label className="wc-field">
              <span>Teamname</span>
              <input value={name} maxLength={60} placeholder="Team Kaffeepause" aria-invalid={tried && !!nameError} onChange={e => setName(e.target.value)} />
              {tried && nameError && <small className="wc-error">{nameError}</small>}
            </label>
          )}
          <TeamAbbreviationField value={shownAbbreviation} error={tried ? abbreviationError : null}
            onChange={value => { setAbbreviationEdited(true); setAbbreviation(value); }} />
        </>
      )}

      <div className="wc-form-actions">
        <button type="button" className="wc-btn wc-btn--ghost" onClick={onCancel}>Abbrechen</button>
        {!blocked && <button type="submit" className="wc-btn" disabled={saving}>{saving ? 'Einen Moment …' : 'Team gründen'}</button>}
      </div>
    </form>
  );
}

/**
 * Abschnitt auf der Veranstalterseite (`OrganizerDetail`): das Team des Vereins in der
 * Winterchallenge. Öffentlich nur, wenn es ein Team gibt; wer den Verein verwaltet und noch
 * keins hat, sieht stattdessen die Einladung, eins zu gründen.
 */
export function OrganizerChallengeSection({ summary, team, canManage, onCreateTeam }: {
  summary: ChallengeSummary;
  team: Pick<ChallengeTeam, 'slug' | 'name' | 'km' | 'rank' | 'teamsTotal'> & { memberCount: number } | null;
  canManage: boolean;
  onCreateTeam?: () => void;
}) {
  if (!team && !canManage) return null;
  return (
    <section className="wc-orgsec">
      <ChallengeArt className="wc-orgsec-art" />
      <div className="wc-orgsec-body">
        <span className="wc-kicker">{summary.title}</span>
        {team ? (
          <>
            <strong className="wc-orgsec-title">Unser Team: {team.name}</strong>
            <div className="wc-orgsec-stats">
              <span><b>{formatKm(team.km)}</b> km</span>
              <span><b>{team.memberCount}</b> {team.memberCount === 1 ? 'fährt mit' : 'fahren mit'}</span>
              {team.rank != null && <span>Platz <b>{team.rank}</b> von {team.teamsTotal.toLocaleString('de-DE')}</span>}
            </div>
            <span className="wc-bar wc-bar--slim" style={{ maxWidth: 360 }}>
              <span className="wc-bar-fill" style={{ width: `${progressPercent(summary.totalKm, summary.goalKm)}%` }} />
            </span>
          </>
        ) : (
          <>
            <strong className="wc-orgsec-title">Mit dem Verein Kilometer sammeln</strong>
            <span className="wc-muted" style={{ marginTop: 2 }}>
              Gründet ein Team für die Winterchallenge. Es zeigt euren Verein und eure nächsten Events allen, die mitfahren.
            </span>
          </>
        )}
      </div>
      {team
        ? <Link className="wc-btn wc-orgsec-cta" to={`/winterchallenge/team/${team.slug}`}>Zum Team<ArrowRight size={16} aria-hidden /></Link>
        : <button type="button" className="wc-btn wc-orgsec-cta" onClick={onCreateTeam}>Team gründen<ArrowRight size={16} aria-hidden /></button>}
    </section>
  );
}

function TeamAbbreviationField({ value, error, onChange }: { value: string; error?: string | null; onChange: (value: string) => void }) {
  return <label className="wc-field">
    <span>Teamkürzel</span>
    <input value={value} maxLength={3} autoCapitalize="characters" autoCorrect="off" spellCheck={false}
      placeholder="ABC" aria-invalid={!!error}
      onChange={e => onChange(Array.from(normalizeTeamAbbreviation(e.target.value)).slice(0, 3).join(''))} />
    {error ? <small className="wc-error">{error}</small> : <small className="wc-hint">Steht vor euren Fahrernamen, zum Beispiel [ABC] Max M.</small>}
  </label>;
}
